import { zodResolver } from "@hookform/resolvers/zod";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { emitNotification, Button } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { NewControlProgram } from "api/generated/model";
import { OrgApplicationBar } from "components/applicationBar/orgApplicationBar";
import { StickyFooter } from "components/stickyFooter/footer";
import { FormFooterWrapper } from "components/stickyFooter/FormFooterWrapper";
import { ViewCancelButton } from "components/stickyFooter/ViewCancelButton";
import { useCurrentUserOverview } from "utils/hooks/useCurrentUserOverview";
import { useUserStatusQuery } from "utils/queries/useUserStatusQuery";

import { useCreateControlProgramMutation } from "../queries/useCreateControlProgramMutation";
import { buildControlProgramViewUrl } from "../urlUtils";

import { ControlProgramForm } from "./ControlProgramForm";
import {
    ControlProgramFormSchema,
    useControlProgramViewFormSchema,
} from "./useControlProgramViewFormSchema";

export function ControlProgramCreatePage() {
    const { t } = useTranslation();
    const { data: userStatus } = useUserStatusQuery();
    const userHasAccess = userStatus.actions.controlProgram.create;

    return (
        <OrgApplicationBar title={t("CreateControlProgram_Title")}>
            {userHasAccess ? <View /> : <NoAccessCard />}
        </OrgApplicationBar>
    );
}

function View() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const schema = useControlProgramViewFormSchema();
    const currentUser = useCurrentUserOverview();

    const redirectToNewlyCreatedProgram = (id: number) => {
        navigate(buildControlProgramViewUrl(id));
    };
    const { mutate, isPending } = useCreateControlProgramMutation(
        redirectToNewlyCreatedProgram
    );

    const { control, formState, handleSubmit, trigger } =
        useForm<ControlProgramFormSchema>({
            resolver: zodResolver(schema),
            mode: "onBlur",
            defaultValues: {
                name: "",
                description: "",
                running: true,
                tagIds: [],
                responsibleId: currentUser?.id,
                accessRights: [{ userId: currentUser?.id, right: "Admin" }],
                controlEntityIds: [],
            },
        });

    const handleSubmitClick = handleSubmit(
        (data: ControlProgramFormSchema) => {
            const creatableControlProgram: NewControlProgram =
                toCreatableControlProgram(data);
            mutate({ data: creatableControlProgram });
        },
        (error) => {
            console.log(error);
            emitNotification(
                "error",
                t("ControlProgram_SubmitValidationError")
            );
        }
    );
    if (!currentUser) {
        return null;
    }

    return (
        <FormFooterWrapper>
            <ControlProgramForm
                control={control}
                trigger={trigger}
                users={[currentUser]}
                canEdit
                canEditAccessRights
                canEditEntities
                linkedEntities={[]}
            />
            <StickyFooter>
                <Button
                    size="large"
                    variant="primary"
                    disabled={!formState.isDirty}
                    loading={isPending}
                    onClick={() => void handleSubmitClick()}
                >
                    {t("CreateControlProgram_Title")}
                </Button>
                <ViewCancelButton
                    isDirty={formState.isDirty}
                    bodyLabel={t("ControlProgram_ConfirmCancel_Body")}
                />
            </StickyFooter>
        </FormFooterWrapper>
    );
}

function toCreatableControlProgram(
    data: ControlProgramFormSchema
): NewControlProgram {
    return {
        name: data.name,
        description: data.description,
        running: data.running,
        tagIds: data.tagIds,
        responsibleId: data.responsibleId,
        userIdAndRights: data.accessRights,
        controlEntityIds: data.controlEntityIds.map((ce) => ce.entityId),
    };
}
